import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import useMediaQuery from "../hooks/useMediaQuery";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  LUSUKU_PROMISE_HEADLINE,
  LUSUKU_PROMISE_MOBILE_HEADLINE,
  LUSUKU_PROMISE_POINTS,
  REFUND_POLICY_PATH,
  REFUND_POLICY_LINK_TEXT,
  HELPLINE_DISPLAY,
  HELPLINE_TEL,
  HELPLINE_ORDER_TEXT,
  WHATSAPP_ORDER_TEXT,
  WHATSAPP_ORDER_URL,
  heroActionsFor,
  promiseSummaryFor,
} from "../config/customerPromise";
import {
  trackPromiseView,
  trackPromisePolicyOpen,
  trackHelplineTap,
  trackHeroAction,
  trackOfferView,
} from "../utils/analytics";
import useHomeFacts from "../hooks/useHomeFacts";
import { useCartCount } from "../redux/cartCount";
import { formatCurrency } from "../config/market";
import useGrowthOffers, { offerToShowOf } from "../hooks/useGrowthOffers";
import { deliveryOfferEventKey } from "../utils/deliveryOffers";

/**
 * The Lusuku promise, in two sizes, both reading config/customerPromise so
 * every surface says the same thing:
 *
 *   PromiseBanner    the homepage's first screen: what Lusuku is, the three
 *                    promises, ordering by phone and the refund policy. It is
 *                    the page's H1. Orientation comes before any advert.
 *   CartPromiseNote  the same promises, compact, beside every cart's total,
 *                    where the doubt about paying is strongest.
 *
 * Plain text and icons only: no images, so the first screen costs no data.
 */

const POINT_ICONS = {
  pay_on_delivery: PaymentsOutlinedIcon,
  check_at_door: FactCheckOutlinedIcon,
  refund_same_way: CurrencyExchangeOutlinedIcon,
};

const PromiseIcon = ({ pointKey }) => {
  const Icon = POINT_ICONS[pointKey] || FactCheckOutlinedIcon;
  return <Icon fontSize="small" aria-hidden="true" />;
};

const HelplineLink = ({ surface, children }) => (
  <a
    className="helpline-call"
    href={HELPLINE_TEL}
    aria-label={`Call ${HELPLINE_DISPLAY}`}
    onClick={() => trackHelplineTap(surface, "call")}
  >
    {children || HELPLINE_DISPLAY}
  </a>
);

/* Ordering by phone, with WhatsApp beside the number when the market has a
   line: one sentence for the homepage and every cart. */
const HelplineOffer = ({ surface, balanced = false }) => (
  <span className="helpline-offer">
    <span className="helpline-prompt">{HELPLINE_ORDER_TEXT}</span>{" "}
    <span className="helpline-links">
      <HelplineLink surface={surface}>
        {balanced ? (
          <>
            <PhoneOutlinedIcon aria-hidden="true" />
            <span className="contact-copy">
              <span>Call us</span>
              <small>{HELPLINE_DISPLAY}</small>
            </span>
          </>
        ) : (
          <>Call {HELPLINE_DISPLAY}</>
        )}
      </HelplineLink>
      {WHATSAPP_ORDER_URL ? (
        <span className="helpline-whatsapp">
          <span className="helpline-or">{" or "}</span>
          <a
            href={WHATSAPP_ORDER_URL}
            aria-label={WHATSAPP_ORDER_TEXT}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackHelplineTap(surface, "whatsapp")}
          >
            {balanced ? (
              <>
                <WhatsAppIcon aria-hidden="true" />
                <span className="contact-copy">
                  <span>WhatsApp</span>
                  <small>Chat to order</small>
                </span>
              </>
            ) : (
              WHATSAPP_ORDER_TEXT
            )}
          </a>
        </span>
      ) : null}
    </span>
  </span>
);

const HERO_ACTION_ICONS = {
  food: RestaurantMenuOutlinedIcon,
  groceries: ShoppingBasketOutlinedIcon,
  cart: ShoppingCartOutlinedIcon,
};

const PolicyLink = ({ surface }) => (
  <Link to={REFUND_POLICY_PATH} onClick={() => trackPromisePolicyOpen(surface)}>
    {REFUND_POLICY_LINK_TEXT}
  </Link>
);

/* The free delivery this shopper can get next, exactly as the server chose it
   (offerToShow on GET /api/growth/offers: a first order, or an invite credit).
   Says the cap, so it never promises more than the server will waive. */
const DeliveryOfferLine = ({ surface }) => {
  const offer = offerToShowOf(useGrowthOffers());
  const eventKey = offer ? deliveryOfferEventKey(offer) : "";
  useEffect(() => {
    if (eventKey) trackOfferView(surface, eventKey);
  }, [eventKey, surface]);
  if (!offer) return null;
  const capText = offer.cap > 0 ? ` (up to ${formatCurrency(offer.cap)})` : "";
  const text =
    offer.kind === "REFERRAL_FREE_DELIVERY"
      ? `Your next delivery is free, thanks to your invite${capText}.`
      : offer.signedIn
        ? `Your first delivery is free${capText}.`
        : `New to Lusuku? Your first delivery is free${capText}.`;
  return <OfferLine>{text}</OfferLine>;
};

export const PromiseBanner = ({ surface = "home" }) => {
  useEffect(() => {
    trackPromiseView(surface);
  }, [surface]);
  const facts = useHomeFacts();
  const cartCount = useCartCount();
  const smallScreen = useMediaQuery("(max-width: 600px)");
  const actions = heroActionsFor({ leadAction: facts?.leadAction, cartCount });

  return (
    <Banner aria-labelledby="lusuku-promise-heading">
      <BannerInner>
        <Intro>
          <Eyebrow>Uganda's online marketplace.</Eyebrow>
          <Headline id="lusuku-promise-heading">
            {smallScreen
              ? LUSUKU_PROMISE_MOBILE_HEADLINE
              : LUSUKU_PROMISE_HEADLINE}
          </Headline>
          <Summary>{promiseSummaryFor(facts?.vendorCount)}</Summary>
          <DeliveryOfferLine surface={surface} />
          <Actions>
            {actions.map((action, index) => {
              const Action = index === 0 ? PrimaryAction : SecondaryAction;
              const Icon = HERO_ACTION_ICONS[action.key];
              return (
                <Action
                  key={action.key}
                  $food={action.key === "food"}
                  to={action.to}
                  aria-label={action.label}
                  onClick={() =>
                    trackHeroAction(surface, action.key, index + 1)
                  }
                >
                  {Icon ? <Icon fontSize="small" aria-hidden="true" /> : null}
                  <span className="action-copy">
                    <span>{action.title || action.label}</span>
                    {action.detail && (
                      <>
                        {" "}
                        <span className="action-detail">{action.detail}</span>
                      </>
                    )}
                  </span>
                </Action>
              );
            })}
          </Actions>
          <PhoneLine>
            <HelplineOffer surface={surface} balanced />
          </PhoneLine>
        </Intro>

        <PromiseCard aria-label="The Lusuku promise">
          <CardLabel>The Lusuku promise</CardLabel>
          <PromiseList>
            {LUSUKU_PROMISE_POINTS.map((point) => (
              <PromiseRow key={point.key}>
                <PromiseTile>
                  <PromiseIcon pointKey={point.key} />
                </PromiseTile>
                <span>
                  <PromiseTitle>{point.title}</PromiseTitle>
                  <PromiseDetail>{point.detail}</PromiseDetail>
                </span>
              </PromiseRow>
            ))}
          </PromiseList>
          <CardFoot>
            <PolicyLink surface={surface} />
          </CardFoot>
        </PromiseCard>
      </BannerInner>
    </Banner>
  );
};

export const CartPromiseNote = ({ surface = "cart" }) => {
  useEffect(() => {
    trackPromiseView(surface);
  }, [surface]);

  return (
    <Note role="note" aria-label="The Lusuku promise">
      <NoteList>
        {LUSUKU_PROMISE_POINTS.map((point) => (
          <li key={point.key}>
            <PromiseIcon pointKey={point.key} />
            <span>{point.title}</span>
          </li>
        ))}
      </NoteList>
      <NoteFoot>
        <PolicyLink surface={surface} />
        <HelplineOffer surface={surface} />
      </NoteFoot>
    </Note>
  );
};

/* ── Homepage banner ───────────────────────────────────────────────────── */

/* Tight under the fixed navbar so the promise, the carousel and the top of
   the category tiles share the first screen. */
const Banner = styled.section`
  background: #2f474e;
  color: #ffffff;
  padding: 20px 16px 28px;

  @media (min-width: 900px) {
    padding: 24px 32px 40px;
  }
`;

const BannerInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  align-items: center;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: 56px;
  }
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
`;

const Eyebrow = styled.span`
  font-family: "Inter", "Segoe UI", sans-serif;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #b9d98f;
`;

const Headline = styled.h1`
  margin: 0;
  font-size: 34px;
  line-height: 1.06;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-wrap: balance;

  @media (min-width: 900px) {
    font-size: 54px;
  }
`;

const Summary = styled.p`
  margin: 0;
  max-width: 34em;
  font-size: 16px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);

  @media (min-width: 900px) {
    font-size: 18px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  width: 100%;

  @media (min-width: 900px) {
    width: auto;
  }
`;

const actionBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  gap: 8px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 800;
  text-decoration: none;
  transition: background-color 150ms ease, transform 150ms ease;
  flex: 1 1 140px;

  svg {
    font-size: 20px;
    flex-shrink: 0;
  }

  .action-copy {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  @media (max-width: 600px) {
    min-height: 60px;
    padding: 10px 12px;
    font-size: 14px;
    line-height: 1.25;

    .action-copy {
      flex-direction: column;
      gap: 2px;
    }

    .action-detail {
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  &:focus-visible {
    outline: 3px solid #b9d98f;
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(1px);
  }

  @media (min-width: 900px) {
    flex: 0 0 auto;
  }
`;

const PrimaryAction = styled(Link)`
  ${actionBase}
  background: ${({ $food }) => ($food ? "#c2410c" : "#c5f23b")};
  color: ${({ $food }) => ($food ? "#ffffff" : "#18342c")};
  box-shadow: 0 6px 18px rgba(8, 22, 26, 0.22);

  svg {
    color: inherit;
  }

  &:hover {
    background: ${({ $food }) => ($food ? "#9a3412" : "#d4ff50")};
    color: ${({ $food }) => ($food ? "#ffffff" : "#18342c")};
  }
`;

/* Category colours follow the destination even when the server changes order. */
const SecondaryAction = styled(Link)`
  ${actionBase}
  background: ${({ $food }) => ($food ? "#e54b0e" : "#c5f23b")};
  color: ${({ $food }) => ($food ? "#ffffff" : "#18342c")};

  svg {
    color: inherit;
  }

  &:hover {
    background: ${({ $food }) => ($food ? "#9a3412" : "#d4ff50")};
    color: ${({ $food }) => ($food ? "#ffffff" : "#18342c")};
  }
`;

const PhoneLine = styled.div`
  width: 100%;
  max-width: 440px;
  margin: 10px 0 0;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);

  .helpline-offer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .helpline-links {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }
  .helpline-whatsapp {
    display: contents;
  }
  .helpline-or {
    display: none;
  }

  a {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    min-height: 44px;
    padding: 4px 0;
    border-radius: 6px;
    color: #ffffff;
    text-decoration: none;
  }

  a:hover {
    color: #d4ff50;
  }
  a:hover .contact-copy > span {
    text-decoration: underline;
    text-underline-offset: 4px;
  }
  a:focus-visible {
    outline: 3px solid #b9d98f;
    outline-offset: 3px;
  }
  svg {
    flex-shrink: 0;
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    padding: 6px;
    color: #c5f23b;
  }
  .helpline-whatsapp svg {
    padding: 0;
  }
  .contact-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
    text-align: left;
  }
  .contact-copy > span {
    font-weight: 700;
    font-size: 14px;
  }
  small {
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.8);
  }

  .helpline-call svg {
    background: #c5f23b;
    border-radius: 50%;
    color: #18342c;
  }
  .helpline-call:hover svg {
    background: #d4ff50;
  }

  @media (max-width: 600px) {
    .helpline-links {
      gap: 12px;
    }
  }
`;

const PromiseCard = styled.div`
  background: #ffffff;
  color: #15232a;
  border-radius: 20px;
  padding: 22px 22px 18px;
  box-shadow: 0 18px 40px rgba(8, 22, 26, 0.28);

  @media (min-width: 900px) {
    padding: 28px 28px 22px;
  }
`;

const CardLabel = styled.h2`
  display: block;
  font-family: "Inter", "Segoe UI", sans-serif;
  font-size: 16px;
  line-height: 1.4;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  color: #3d6a0b;
  margin: 0 0 6px;
`;

const PromiseList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const PromiseRow = styled.li`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 0;

  @media (min-width: 900px) {
    padding: 14px 0;
  }

  & + & {
    border-top: 1px solid #edf0ea;
  }
`;

const PromiseTile = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #c5f23b;
  color: #18342c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PromiseTitle = styled.strong`
  display: block;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.3;
`;

const PromiseDetail = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 14px;
  line-height: 1.4;
  color: #55646a;
`;

const CardFoot = styled.div`
  padding-top: 12px;
  border-top: 1px solid #edf0ea;
  font-size: 14px;

  a {
    color: #24525f;
    font-weight: 700;
    text-decoration: none;
  }

  a::after {
    content: " \\2192";
  }

  a:hover {
    text-decoration: underline;
  }
`;

const OfferLine = styled.p`
  margin: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: #b9d98f;
  color: #15232a;
  font-size: 14px;
  font-weight: 800;
`;

/* ── Cart note ─────────────────────────────────────────────────────────── */

const Note = styled.div`
  margin: 8px 0;
  padding: 12px;
  border-radius: 12px;
  background: #f3f7ef;
  color: #15232a;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;

  a {
    color: #24525f;
    font-weight: 700;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const NoteList = styled.ul`
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  li {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px 5px 8px;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid #e1e8da;
    font-weight: 700;
    font-size: 12.5px;
  }

  svg {
    color: #3d6a0b;
    font-size: 16px;
  }
`;

const NoteFoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  color: #55646a;
`;
